/**
 * Landing rules — sections/landing-rules.liquid.
 *
 * Two jobs, each a replacement for part of live's scripted `custom_liquid` sections:
 * - Rules. Each `[data-landing-rule]` names a URL parameter and value. When the visitor's URL carries that value
 *   (compared exactly, as live compares it) and not the rule's exception, its sections and blocks are marked
 *   `data-landing-rules-state="shown"` or `"hidden"` (the section's inline CSS acts on the mark) and a section can move
 *   after another. Every matching rule applies, in block order, except that a rule marked `data-stop` ends the list
 *   when it applies (live's if / else-if chain: the first match wins). Rules limited to a page are decided by the
 *   server (sections/landing-rules.liquid): only the ones that can apply on this page reach this script.
 * - Product view events. Each `[data-landing-tracking]` holds a product as JSON. A click on an element matching its
 *   selector pushes `{ ecommerce: null }` and then the event, as live's Carrara script does. The listener is on the
 *   document in the capture phase, so elements added later (Swiper's loop clones) are covered and the event is in the
 *   dataLayer before any click handler of the page runs.
 *
 * @extends HTMLElement
 */
class LandingRules extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    const params = new URL(window.location.href).searchParams;
    for (const rule of this.querySelectorAll('[data-landing-rule]')) {
      if (!this.ruleApplies(rule.dataset, params)) continue;
      this.apply(rule.dataset);
      if ('stop' in rule.dataset) break;
    }

    this.trackers = [...this.querySelectorAll('[data-landing-tracking]')].flatMap((node) => {
      try {
        const { selector, event, currency } = node.dataset;
        return [{ selector, event, currency, product: JSON.parse(node.textContent) }];
      } catch (error) {
        console.error('landing-rules: unreadable product event data', error);
        return [];
      }
    });
    if (this.trackers.length) document.addEventListener('click', this.onClick, true);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClick, true);
  }

  /**
   * @param {DOMStringMap} rule
   * @param {URLSearchParams} params
   * @returns {boolean} Whether the URL asks for this rule.
   */
  ruleApplies(rule, params) {
    if (!rule.parameter || !rule.value || params.get(rule.parameter) !== rule.value) return false;
    if (rule.exceptParameter && rule.exceptValue && params.get(rule.exceptParameter) === rule.exceptValue) return false;
    return true;
  }

  /** @param {DOMStringMap} rule */
  apply(rule) {
    for (const element of this.targets(rule.showSections, rule.showBlocks)) element.dataset.landingRulesState = 'shown';
    for (const element of this.targets(rule.hideSections, rule.hideBlocks))
      element.dataset.landingRulesState = 'hidden';

    const moving = rule.moveSection && this.section(rule.moveSection);
    const anchor = rule.moveAfter && this.section(rule.moveAfter);
    if (!moving || !anchor || moving === anchor) return;
    if (anchor.nextElementSibling !== moving) anchor.after(moving);
    if ('moveFlatten' in rule) moving.dataset.landingRulesMoved = 'flat';
  }

  /**
   * @param {string} [sectionKeys] - Comma-separated template section keys.
   * @param {string} [blockIds] - Comma-separated block ids, as printed in `data-lp-block`.
   * @returns {Element[]}
   */
  targets(sectionKeys = '', blockIds = '') {
    const sections = this.list(sectionKeys).map((key) => this.section(key));
    const blocks = this.list(blockIds).flatMap((id) => [
      ...document.querySelectorAll(`[data-lp-block="${CSS.escape(id)}"]`),
    ]);
    return [...sections, ...blocks].filter(Boolean);
  }

  /** @param {string} key - A template section key. */
  section(key) {
    return document.querySelector(`[id$="__${CSS.escape(key)}"]`);
  }

  /** @param {string} value */
  list(value) {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  /** @param {MouseEvent} event */
  onClick(event) {
    if (!(event.target instanceof Element)) return;
    for (const tracker of this.trackers) {
      let element = null;
      try {
        element = event.target.closest(tracker.selector);
      } catch (error) {
        console.error(`landing-rules: invalid selector "${tracker.selector}"`, error);
      }
      if (element) this.push(tracker, element);
    }
  }

  /**
   * @param {{ event: string, currency: string, product: object }} tracker
   * @param {Element} element - The clicked element; its text is the event's `cta_text`.
   */
  push(tracker, element) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: tracker.event,
      event_id: `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      event_time: new Date().toISOString(),
      cta_text: element instanceof HTMLElement ? element.innerText.trim() : '',
      ecommerce: {
        currencyCode: tracker.currency,
        detail: {
          actionField: { list: tracker.product.list },
          products: [tracker.product],
        },
      },
    });
  }
}

if (!customElements.get('landing-rules')) {
  customElements.define('landing-rules', LandingRules);
}
